import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { Channel, Event as StreamEvent, StreamChat } from 'stream-chat';

import { fetchAccessibleBidRequestIds } from '@api/BidRequestsApi';
import { GqlClient, setupStream } from '@services/StreamService';
import { UserContext } from '@providers/UserProvider/UserProvider';
import LaunchDarklyService from '@services/LaunchDarklyService';
import { requestIdFromChannelId } from '@utils/chat';

import {
  applyChannelCount,
  markChannelRead,
  removeChannel,
  sumCounts,
  takeEventChannels,
  UnreadEvent
} from './unreadState';
import { useHiddenUnread } from './useHiddenUnread';

interface ConversationsProviderProps {
  children: ReactNode;
  gqlClient: GqlClient;
}

interface ConversationsState {
  activeChannel: Channel | undefined;
  filteredUnreadCount: number | undefined;
  newActiveChannel: Channel | undefined;
  showNewChannelModal: boolean;
  showSettingsMenu: boolean;
  streamChatToken: string | undefined;
  streamClient: StreamChat | undefined;
  streamReconnecting: boolean;
  unreadChannelMap: Record<string, number>;
  unreadCount: number | undefined;
}

export type ConversationsContextValue = ConversationsState & {
  gqlClient: GqlClient;
  setProperty: (property: Partial<ConversationsState>) => void;
};

const DEFAULT_STATE: ConversationsState = {
  activeChannel: undefined,
  filteredUnreadCount: undefined,
  newActiveChannel: undefined,
  showNewChannelModal: false,
  showSettingsMenu: false,
  streamChatToken: undefined,
  streamClient: undefined,
  streamReconnecting: false,
  unreadChannelMap: {},
  unreadCount: undefined,
};

const normalizeChannelId = (id: string) =>
  id.replace('messaging:', '').replace('bid-request-announcements:', '');

const toUnreadEvent = (event: StreamEvent, userId: string): UnreadEvent => ({
  channelId: event.channel_id && normalizeChannelId(event.channel_id),
  isMe: event.user?.id === userId,
  totalUnreadCount: event.total_unread_count,
  unreadMessages: event.unread_messages
});

const filterByAccessible = (
  channelMap: Record<string, number>,
  inaccessibleIds: Set<string>
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(channelMap).filter(([id]) => {
      const requestId = requestIdFromChannelId(id);
      return !requestId || !inaccessibleIds.has(requestId);
    })
  );

export const ConversationsContext = createContext<ConversationsContextValue>({
  ...DEFAULT_STATE,
  gqlClient: {
    mutate: () => Promise.resolve({ data: undefined }),
    query: () => Promise.resolve({ data: { streamChatToken: '' } }),
  },
  setProperty: () => {}
});

const ConversationsProvider = ({ children, gqlClient }: ConversationsProviderProps) => {
  const user = useContext(UserContext);
  const ldClient = useLDClient();
  // ldClient is undefined until withLDProvider resolves it after mount (see EventsList.tsx).
  const isReady = !!ldClient;
  const [state, setState] = useState<ConversationsState>(DEFAULT_STATE);
  const streamClientRef = useRef<StreamChat | undefined>(undefined);
  const queryClient = useQueryClient();
  const accessibleIdsRef = useRef<Set<string>>(new Set());
  const inaccessibleIdsRef = useRef<Set<string>>(new Set());
  const pendingAccessChecksRef = useRef<Set<string>>(new Set());
  const hiddenUnread = useHiddenUnread();

  const setProperty = useCallback((updates: Partial<ConversationsState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const updateUnreadState = useCallback((
    channelId: string | undefined,
    totalUnreadCount: number | undefined,
    updateFn: (prev: Record<string, number>) => number
  ) => {
    if (totalUnreadCount === 0) hiddenUnread.clear();
    const hiddenTotal = hiddenUnread.total();

    setState(prev => ({
      ...prev,
      ...applyChannelCount(prev, channelId, updateFn(prev.unreadChannelMap), totalUnreadCount, hiddenTotal)
    }));
  }, [hiddenUnread]);

  // Moves an event's channels out of the badge once it turns out the user can't open that event.
  const hideEventChannels = useCallback((requestId: string) => {
    setState(prev => {
      const taken = takeEventChannels(prev, requestId);
      if (!taken) return prev;

      hiddenUnread.merge(taken.hidden);
      return { ...prev, ...taken.next };
    });
  }, [hiddenUnread]);

  // Events with no unread messages at page load were never checked for access, so check them the
  // first time a message arrives. The message stays counted until the check says otherwise.
  const checkEventAccess = useCallback((requestId: string) => {
    if (pendingAccessChecksRef.current.has(requestId)) return;
    pendingAccessChecksRef.current.add(requestId);

    queryClient.fetchQuery({
      queryFn: async () => {
        const { accessibleIds, success } = await fetchAccessibleBidRequestIds([requestId]);
        if (!success || !accessibleIds) throw new Error('Could not check event access');
        return (accessibleIds as number[]).map(String).includes(requestId);
      },
      queryKey: ['accessibleEventId', requestId],
      staleTime: Infinity,
    })
      .then(isAccessible => {
        if (isAccessible) {
          accessibleIdsRef.current.add(requestId);
          return;
        }
        inaccessibleIdsRef.current.add(requestId);
        hideEventChannels(requestId);
      })
      // On failure keep the message counted, matching a failed check at page load.
      .catch(() => undefined)
      .finally(() => pendingAccessChecksRef.current.delete(requestId));
  }, [hideEventChannels, queryClient]);

  // The Stream listener is subscribed once in the [isReady] effect, so every handler must only read
  // refs and stable callbacks.
  const streamEventHandlers = useMemo((): Record<string, (event: UnreadEvent) => void> => {
    const handleNewMessage = ({ channelId, isMe, totalUnreadCount }: UnreadEvent) => {
      if (isMe) return;
      const requestId = requestIdFromChannelId(channelId ?? '');
      if (requestId && inaccessibleIdsRef.current.has(requestId)) {
        hiddenUnread.increment(channelId as string);
        return;
      }
      updateUnreadState(channelId, totalUnreadCount, prev => (prev[channelId as string] || 0) + 1);
      if (requestId && !accessibleIdsRef.current.has(requestId)) checkEventAccess(requestId);
    };

    const handleMarkRead = ({ channelId, totalUnreadCount }: UnreadEvent) => {
      hiddenUnread.markRead(channelId);
      updateUnreadState(channelId, totalUnreadCount, () => 0);
    };

    const handleMessageRead = ({ channelId, isMe }: UnreadEvent) => {
      if (!isMe) return;
      setState(prev => ({ ...prev, ...markChannelRead(prev, channelId) }));
    };

    const handleMarkUnread = ({ channelId, totalUnreadCount, unreadMessages }: UnreadEvent) => {
      updateUnreadState(channelId, totalUnreadCount, () => unreadMessages ?? 0);
    };

    const handleRemovedFromChannel = ({ channelId, totalUnreadCount }: UnreadEvent) => {
      hiddenUnread.remove(channelId);
      const hiddenTotal = hiddenUnread.total();
      setState(prev => ({ ...prev, ...removeChannel(prev, channelId, totalUnreadCount, hiddenTotal) }));
    };

    return {
      'message.new': handleNewMessage,
      'message.read': handleMessageRead,
      'notification.mark_read': handleMarkRead,
      'notification.mark_unread': handleMarkUnread,
      'notification.message_new': handleNewMessage,
      'notification.removed_from_channel': handleRemovedFromChannel,
    };
  }, [checkEventAccess, hiddenUnread, updateUnreadState]);

  const handleStreamEvent = useCallback((event: StreamEvent) => {
    const handler = streamEventHandlers[event.type];
    if (!handler) return;
    handler(toUnreadEvent(event, user.id.toString()));
  }, [streamEventHandlers, user.id]);

  useEffect(() => {
    if (!isReady) return undefined;

    let unsubConnectionChanged: (() => void) | undefined;
    let unsubStreamEvents: (() => void) | undefined;

    const init = async () => {
      // Identify the user to LaunchDarkly before chat init so any LD-gated chat behaviour sees the
      // real context rather than the stale anonymous one; a rejection (LD unreachable) is swallowed
      // so it degrades gracefully instead of killing chat.
      await LaunchDarklyService.configureUser(ldClient, user)?.catch(() => undefined);

      const streamClient = await setupStream({ setState: setProperty }, user);
      streamClientRef.current = streamClient;

      const connectionSub = streamClient.on('connection.changed', ({ online }: StreamEvent) => {
        setState(prev => ({ ...prev, streamReconnecting: !online }));
      });
      unsubConnectionChanged = connectionSub.unsubscribe;

      if (user.subdomain === 'synthetics') return;

      const { channels, total_unread_count: totalUnreadCount } = await streamClient.getUnreadCount();
      const unreadChannelMap = Object.fromEntries(
        channels.map(c => [
          // GetStream is not consistent with channel_id format, so we normalize it
          normalizeChannelId(c.channel_id),
          c.unread_count
        ])
      );

      // Check which channels with unread messages still belong to accessible events.
      // This filters out deleted events (async channel removal not yet processed) and
      // hidden events (buyer-only dashboard preference).
      const unreadBidRequestIds = [...new Set(
        Object.entries(unreadChannelMap)
          .filter(([, count]) => count > 0)
          .map(([id]) => requestIdFromChannelId(id))
          .filter((id): id is string => !!id)
      )];

      let filteredMap = unreadChannelMap;
      if (unreadBidRequestIds.length > 0) {
        const { accessibleIds: rawAccessibleIds, success } = await fetchAccessibleBidRequestIds(unreadBidRequestIds);
        if (success && rawAccessibleIds) {
          const accessibleIds = new Set((rawAccessibleIds as number[]).map(String));
          accessibleIdsRef.current = accessibleIds;
          inaccessibleIdsRef.current = new Set(unreadBidRequestIds.filter(id => !accessibleIds.has(id)));
        }
        filteredMap = filterByAccessible(unreadChannelMap, inaccessibleIdsRef.current);
      }

      hiddenUnread.replace(Object.fromEntries(
        Object.entries(unreadChannelMap).filter(([id]) => !(id in filteredMap))
      ));
      // getUnreadCount only lists the 100 most recent unread channels, so prefer Stream's total
      // over summing the map; the map is the fallback when the total is missing.
      const visibleTotal = totalUnreadCount === undefined
        ? sumCounts(filteredMap)
        : Math.max(0, totalUnreadCount - hiddenUnread.total());
      setState(prev => ({ ...prev, unreadChannelMap: filteredMap, unreadCount: visibleTotal }));

      // Add GetStream event listener after initial state is set
      const eventSub = streamClient.on(event => handleStreamEvent(event));
      unsubStreamEvents = eventSub.unsubscribe;
    };

    init().catch(() => {
      setState(prev => ({ ...prev, streamReconnecting: true }));
    });

    return () => {
      unsubConnectionChanged?.();
      unsubStreamEvents?.();
      streamClientRef.current?.disconnectUser();
    };
  // isReady is the only dep — init() already awaits identify() before reading the flag, so
  // re-running on a flag change would race a mid-flight init() into two live StreamChat clients.
  }, [isReady]);

  const contextValue = useMemo<ConversationsContextValue>(
    () => ({ ...state, gqlClient, setProperty }),
    [gqlClient, state, setProperty]
  );

  return (
    <ConversationsContext.Provider value={contextValue}>
      {children}
    </ConversationsContext.Provider>
  );
};

export { ConversationsProvider };
